import { useState, useCallback, useRef, useEffect, RefObject, SetStateAction, Dispatch, useMemo, useLayoutEffect } from "react"
import Popper, { PopperPlacementType } from "@mui/material/Popper"
import { MobergAnimationCurve, MobergAnimationTiming } from "../Components/MobergAnimation/MobergAnimation"
import React from "react"
import { MobergBoxShadow } from "../Components/MobergThemes/MobergStyles"
import { CSSProperties } from "styled-components"
import { useOnMount } from "./useOnMount"
import { Modifier, VirtualElement } from "@popperjs/core"
import ResizeObserver from 'resize-observer-polyfill';


type usePopoverProps = {
	placement: PopperPlacementType
	anchorRef?: RefObject<any>
	clickOutsideClose?: boolean
	zIndex?: number
	useAnchorAsMinWidth?: boolean
	isContextMenu?: boolean
}

type PopoverComponentProps = {
	style?: CSSProperties
}

type Coordinates = {
	x: number
	y: number
}

export type MobergMenuController = {
	isOpen: boolean
	open: (event: React.MouseEvent<Element>) => void
	close: () => void
	setClickOutsideClose: Dispatch<SetStateAction<boolean>>
	MobergMenu: React.FC<PopoverComponentProps>
}

export const useMobergMenu = ({ placement, anchorRef, zIndex, useAnchorAsMinWidth, isContextMenu }: usePopoverProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [anchorEl, setAnchorEl] = useState<Element | null>(anchorRef?.current ? anchorRef.current : null)
	const popoverRef = useRef<HTMLDivElement>(null)
	const mouseDownTarget = useRef<any>(null)
	const isMounted = useRef<boolean>()
	const [clickOutsideClose, setClickOutsideClose] = useState<boolean>(true)
	const [minWidth, setMinWidth] = useState<number>()
	const [coordinates, setCoordinates] = useState<Coordinates>()

	const virtualAnchor: VirtualElement = useMemo(() => ({
		getBoundingClientRect: () => ({
			top: coordinates?.y ?? 0,
			left: coordinates?.x ?? 0,
			bottom: 0,
			right: 0,
			width: 0,
			height: 0,
			x: coordinates?.x ?? 0,
			y: coordinates?.y ?? 0,
			toJSON: () => {}
		}),
	}), [coordinates])

	const close = useCallback(async () => {
		if (isContextMenu) {
			setIsOpen(false)
			return
		}

		const popover = popoverRef.current

		if (!popover) {
			return
		}

		popover.style.transition = `all ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_OUT}`
		popover.style.opacity = "0"
		popover.style.transform = "scale(0.9)"

		await new Promise<void>(resolve => {
			popover.ontransitionend = () => {
				// Prevent this from being called more than once.
				popover.ontransitionend = null

				// By waiting a little bit after the animation to finishes, we can make sure the animation is smooth.
				setTimeout(() => {
					if (isMounted.current) {
						setIsOpen(false)
					}
					setTimeout(resolve) // wait for the Recoil State to update
				})
			}
		})
	}, [isContextMenu])

	const open = useCallback(
		(event?: React.MouseEvent<Element>) => {
			if (isOpen) {
				close()
				return
			}

			if (event?.clientX && event?.clientY) {
				setCoordinates({ x: event.clientX, y: event.clientY })
			}

			if (isContextMenu) {
				setIsOpen(true)
				return
			}

			setIsOpen(previous => {
				const newOpenState = !previous

				if (newOpenState) {
					setTimeout(() => {
						const popover = popoverRef.current

						if (popover) {
							popover.style.transition = `all ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN}`
							popover.style.opacity = "1"
							popover.style.transform = "scale(1)"
						}
					})
				}

				return newOpenState
			})

			if (!anchorRef?.current) {
				setAnchorEl(event?.currentTarget ?? null)
			} else {
				setAnchorEl(anchorRef.current)
			}
		}, [anchorRef, close, isContextMenu, isOpen])

	const MobergMenu: React.FC<PopoverComponentProps> = useMemo(() => ({ children, style }) => {
		const getOrigin = (placement?: string): string => {
			switch (placement) {
				case "top-start": return "bottom left"
				case "top-end": return "bottom right"
				case "top": return "bottom"
				case "left": return "center right"
				case "left-end": return "bottom right"
				case "left-start": return "top right"
				case "right": return "center left"
				case "right-end": return "bottom left"
				case "right-start": return "top left"
				case "bottom-end": return "top right"
				case "bottom": return "top"
				case "bottom-start": return "top left"
				default:
					return "bottom"
			}
		}

		const flipAnimationCorrection: Modifier<"flipAnimationCorrection", {}> = {
			name: "flipAnimationCorrection",
			enabled: true,
			phase: "beforeWrite",
			fn: ({ state }) => {
				if (state.placement !== state.options.placement) {
					// This means that the popper is flipped and we have to update the origin for the animation to look correct.
					if (popoverRef.current) {
						popoverRef.current.style.transformOrigin = getOrigin(state.placement)
					}
				}
			},
		}

		return (
			<Popper
				open={isOpen}
				placement={placement}
				anchorEl={isContextMenu ? virtualAnchor : anchorEl}
				style={{
					zIndex: zIndex ?? 10000,
					minWidth: useAnchorAsMinWidth ? minWidth : "unset"
				}}
				modifiers={[
					{
						name: "flip",
						enabled: true,
					},
					{
						name: "preventOverflow",
						options: {
							altAxis: true // false by default
						},
					},
					flipAnimationCorrection
				]}
				onWheel={event => event.stopPropagation()}>
				<div
					style={{
						opacity: isContextMenu || (isOpen && popoverRef.current) ? 1 : 0,
						transform: isContextMenu || (isOpen && popoverRef.current) ? "scale(1)" : "scale(0.95)",
						transformOrigin: getOrigin(placement),
						transition: `scale ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN}`,
				background: "var(--color-surface-menu)",
				color: "var(--text-color)",
				borderRadius: "4px",
				boxShadow: MobergBoxShadow.REGULAR,
						padding: isContextMenu ? "2px": "8px",
						...style
					}}
					ref={popoverRef}
				>
					{children}
				</div>
			</Popper>
		)
	}, [isOpen, placement, isContextMenu, virtualAnchor, anchorEl, zIndex, useAnchorAsMinWidth, minWidth])

	useEffect(() => {
		const updateMouseDown = (event: MouseEvent) => {
			mouseDownTarget.current = event.target
		}

		const clickOutsideCloseHandler = (event: MouseEvent) => {
			if (isContextMenu) {
				close()
				mouseDownTarget.current = null
				return
			}

			const clickedInsidePopover = popoverRef.current?.contains(event.target as Node)
			const clickedInsideAnchor = anchorEl?.contains(event.target as Node)
			const clickedInInput = mouseDownTarget.current instanceof HTMLInputElement

			if (clickOutsideClose && !clickedInsidePopover && !clickedInsideAnchor && !clickedInInput) {
				close()
			}

			mouseDownTarget.current = null
		}

		const closeOnInteractionOutside = (event: Event) => {
			if (!popoverRef.current?.contains(event.target as Node)) {
				close()
			}
		}

		if (isOpen) {
			// Capture mode ensures we detect mousedown before input focus changes
			document.addEventListener("mousedown", updateMouseDown, true)
			document.addEventListener("contextmenu", clickOutsideCloseHandler, true)
			document.addEventListener("click", clickOutsideCloseHandler)

			if (isContextMenu) {
				document.addEventListener("mousedown", closeOnInteractionOutside, true)
				document.addEventListener("wheel", closeOnInteractionOutside, true)
			}
		}

		return () => {
			document.removeEventListener("mousedown", updateMouseDown, true)
			document.removeEventListener("contextmenu", clickOutsideCloseHandler, true)
			document.removeEventListener("click", clickOutsideCloseHandler)
			document.removeEventListener("mousedown", closeOnInteractionOutside, true)
			document.removeEventListener("wheel", closeOnInteractionOutside, true)
		}
	}, [anchorEl, isOpen, close, clickOutsideClose, isContextMenu])

	// Manage mounted state to prevent state updates to unmounted components.
	useOnMount(() => {
		isMounted.current = true

		return () => {
			isMounted.current = false
		}
	})

	useLayoutEffect(() => {
		let observer: ResizeObserver | undefined = undefined

		if (anchorEl && useAnchorAsMinWidth && anchorEl instanceof HTMLElement) {
			setMinWidth(anchorEl.offsetWidth)
			observer = new ResizeObserver(() => setMinWidth(anchorEl.offsetWidth))
			observer.observe(anchorEl)
		}

		return () => observer?.disconnect()

	}, [anchorEl, useAnchorAsMinWidth])

	return { isOpen, open, close, setClickOutsideClose, MobergMenu }
}
